import {
  Component, forwardRef, input, output, signal,
  ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';

/**
 * Inline time picker — 12-hour display, 24-hour "HH:mm" value.
 * Implements ControlValueAccessor for formControlName / ngModel.
 *
 * Design principle: the hour/minute <input> elements are UNCONTROLLED
 * while the user is typing. Angular never writes [value] during active
 * input — only on writeValue (external model change), blur, and arrow-key
 * increments. This avoids the Angular binding fighting the user's keystrokes.
 */
@Component({
  selector: 'app-time-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, SelectButtonModule],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimeInputComponent),
    multi: true
  }],
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.css'
})
export class TimeInputComponent implements ControlValueAccessor, AfterViewInit {

  placeholder = input<string>('HH:MM');
  showIcon    = input<boolean>(true);
  timeChange  = output<string>();

  // Internal state
  hour24   = signal<number | null>(null);
  minute   = signal<number | null>(null);
  period   = signal<'AM' | 'PM'>('AM');
  disabled = signal(false);
  focused  = signal(false);
  touched  = signal(false);
  hasValue = signal(false);

  readonly periodOptions = [
    { label: 'AM', value: 'AM' },
    { label: 'PM', value: 'PM' }
  ];

  @ViewChild('hourInput')   hourInputEl!:   ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput') minuteInputEl!: ElementRef<HTMLInputElement>;

  private onChange:   (v: string) => void = () => {};
  private onTouched:  () => void          = () => {};

  // Holds a writeValue call that arrived before ngAfterViewInit
  private _pendingWrite: string | null | undefined = undefined;
  private _viewReady = false;

  ngAfterViewInit(): void {
    this._viewReady = true;
    if (this._pendingWrite !== undefined) {
      this._applyDomUpdate(this._pendingWrite);
      this._pendingWrite = undefined;
    }
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────

  writeValue(value: string | null | undefined): void {
    if (!value) {
      this.hour24.set(null);
      this.minute.set(null);
      this.period.set('AM');
      this.hasValue.set(false);
    } else {
      const parts = value.split(':');
      if (parts.length >= 2) {
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (!isNaN(h) && !isNaN(m)) {
          this.hour24.set(this.clampH24(h));
          this.minute.set(this.clampMin(m));
          this.period.set(h >= 12 ? 'PM' : 'AM');
          this.hasValue.set(true);
        }
      }
    }

    if (this._viewReady) {
      this._applyDomUpdate(value);
    } else {
      this._pendingWrite = value;
    }
  }

  registerOnChange(fn: (v: string) => void): void { this.onChange  = fn; }
  registerOnTouched(fn: () => void): void          { this.onTouched = fn; }
  setDisabledState(d: boolean): void               { this.disabled.set(d); }

  // ─── DOM update (imperative — never via [value] binding) ─────────────────

  private _applyDomUpdate(value: string | null | undefined): void {
    if (!this.hourInputEl || !this.minuteInputEl) return;
    if (!value) {
      this.hourInputEl.nativeElement.value   = '';
      this.minuteInputEl.nativeElement.value = '';
    } else {
      const h24 = this.hour24();
      const m   = this.minute();
      if (h24 !== null) {
        const h12 = h24 % 12 || 12;
        this.hourInputEl.nativeElement.value = h12.toString().padStart(2, '0');
      }
      if (m !== null) {
        this.minuteInputEl.nativeElement.value = m.toString().padStart(2, '0');
      }
    }
  }

  // ─── Focus / Blur ────────────────────────────────────────────────────────

  onSegmentFocus(segment: 'hour' | 'minute', event: FocusEvent): void {
    this.focused.set(true);
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select(), 0);
  }

  onSegmentBlur(_segment: 'hour' | 'minute', event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    const container = (event.target as HTMLElement).closest('.app-time-input');

    if (!container || !relatedTarget || !container.contains(relatedTarget)) {
      this.focused.set(false);
      if (!this.touched()) { this.touched.set(true); this.onTouched(); }
    }

    if (_segment === 'hour') {
      this._formatHourOnBlur(event.target as HTMLInputElement);
    } else {
      this._formatMinuteOnBlur(event.target as HTMLInputElement);
    }
  }

  onContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('p-selectButton') || target.closest('input')) return;
    this.hourInputEl?.nativeElement.focus();
  }

  // ─── Keyboard ────────────────────────────────────────────────────────────

  onHourKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp')   { event.preventDefault(); this._incrHour(1);  }
    if (event.key === 'ArrowDown') { event.preventDefault(); this._incrHour(-1); }
    if (event.key === 'a' || event.key === 'A') { event.preventDefault(); this.period.set('AM'); this._emit(); }
    if (event.key === 'p' || event.key === 'P') { event.preventDefault(); this.period.set('PM'); this._emit(); }
    if (event.key === ':' || event.key === 'Enter') { event.preventDefault(); this.minuteInputEl?.nativeElement.focus(); }
  }

  onMinuteKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp')   { event.preventDefault(); this._incrMin(1);  }
    if (event.key === 'ArrowDown') { event.preventDefault(); this._incrMin(-1); }
    if (event.key === 'a' || event.key === 'A') { event.preventDefault(); this.period.set('AM'); this._emit(); }
    if (event.key === 'p' || event.key === 'P') { event.preventDefault(); this.period.set('PM'); this._emit(); }
  }

  // ─── Input handlers ──────────────────────────────────────────────────────
  // The inputs are UNCONTROLLED during typing — we never write [value] here.
  // We only read what the user typed.

  onHourInput(event: Event): void {
    const input  = event.target as HTMLInputElement;
    let valStr   = input.value.replace(/\D/g, '').slice(0, 2);
    input.value  = valStr; // strip non-digits only

    if (valStr === '') {
      this.hour24.set(null);
      this.hasValue.set(this.minute() !== null);
      this._emit();
      return;
    }

    // "0" alone: wait — user may be typing "08", "09" etc.
    // Don't commit yet; let the next digit complete the entry.
    if (valStr === '0') return;

    const val = parseInt(valStr, 10);
    if (isNaN(val)) return;

    this._setHour12(val);
    // Auto-advance to minutes when 2 digits entered, or first digit ≥ 2
    // (single digit ≥ 2 can't be followed by a valid second digit in 12h)
    if (valStr.length === 2 || val >= 2) {
      this.minuteInputEl?.nativeElement.focus();
    }
  }

  onMinuteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let valStr  = input.value.replace(/\D/g, '').slice(0, 2);
    input.value = valStr;

    if (valStr === '') {
      this.minute.set(null);
      this.hasValue.set(this.hour24() !== null);
      this._emit();
      return;
    }

    const val = parseInt(valStr, 10);
    if (!isNaN(val)) {
      this.minute.set(this.clampMin(val));
      if (this.hour24() === null) { this.hour24.set(12); this.period.set('PM'); }
      this.hasValue.set(true);
      this._emit();
    }
  }

  // ─── Period toggle ───────────────────────────────────────────────────────

  onPeriodChange(newPeriod: 'AM' | 'PM'): void {
    if (newPeriod === this.period()) return;
    this.period.set(newPeriod);
    const h = this.hour24();
    if (h !== null) {
      if (newPeriod === 'PM' && h < 12)  this.hour24.set(h + 12);
      if (newPeriod === 'AM' && h >= 12) this.hour24.set(h - 12);
      this._emit();
    }
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _setHour12(h12: number): void {
    // In 12-hour format, valid input is 1–12 (0 is treated as 12 = midnight/noon)
    const clamped = h12 === 0 ? 12 : Math.min(12, Math.max(1, h12));
    const p = this.period();
    const h24 = p === 'AM'
      ? (clamped === 12 ? 0   : clamped)
      : (clamped === 12 ? 12  : clamped + 12);
    this.hour24.set(h24);
    if (this.minute() === null) this.minute.set(0);
    this.hasValue.set(true);
    this._emit();
  }

  private _incrHour(delta: number): void {
    const next = ((this.hour24() ?? 0) + delta + 24) % 24;
    this.hour24.set(next);
    this.period.set(next >= 12 ? 'PM' : 'AM');
    if (this.minute() === null) this.minute.set(0);
    this.hasValue.set(true);
    this._emit();
    if (this.hourInputEl) {
      this.hourInputEl.nativeElement.value = (next % 12 || 12).toString().padStart(2, '0');
    }
  }

  private _incrMin(delta: number): void {
    const next = ((this.minute() ?? 0) + delta + 60) % 60;
    this.minute.set(next);
    if (this.hour24() === null) this.hour24.set(0);
    this.hasValue.set(true);
    this._emit();
    if (this.minuteInputEl) {
      this.minuteInputEl.nativeElement.value = next.toString().padStart(2, '0');
    }
  }

  private _formatHourOnBlur(input: HTMLInputElement): void {
    const raw = input.value.replace(/\D/g, '');

    if (raw === '' || raw === '0') {
      // "0" alone → treat as 12 (midnight or noon)
      if (raw === '0') this._setHour12(12);
    }

    const h = this.hour24();
    input.value = h !== null ? (h % 12 || 12).toString().padStart(2, '0') : '';
  }

  private _formatMinuteOnBlur(input: HTMLInputElement): void {
    const m = this.minute();
    input.value = m !== null ? m.toString().padStart(2, '0') : '';
  }

  private _emit(): void {
    const h = this.hour24();
    const m = this.minute();
    if (h === null || m === null) {
      this.onChange('');
      this.timeChange.emit('');
      return;
    }
    const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    this.onChange(value);
    this.timeChange.emit(value);
  }

  private clampH24(h: number): number { return Math.max(0, Math.min(23, h)); }
  private clampMin(m: number): number  { return Math.max(0, Math.min(59, m)); }
}
